import { useInfiniteQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { MotionConfig } from 'motion/react';
import { useEffect } from 'react';
import type { Project, Workspace } from 'sdk';
import { useBreakpointBelow } from '~/hooks/use-breakpoints';
import { usePreloadLazyComponents } from '~/hooks/use-preload-lazy-components';
import { BlockNoteFullHtml } from '~/modules/common/blocknote/lazy-full-html';
import { useBoardStore } from '~/modules/common/board/board-store';
import { useDialoger } from '~/modules/common/dialoger/use-dialoger';
import { projectsListQueryOptions } from '~/modules/project/query';
import type { EnrichedProject } from '~/modules/project/types';
import { BoardEmpty } from '~/modules/task/board/board-empty';
import { BoardHeader } from '~/modules/task/board/board-header';
import { BoardSkeleton } from '~/modules/task/board/board-skeleton';
import { ProjectBoard } from '~/modules/task/board/project-board';
import { WorkspaceBoard } from '~/modules/task/board/workspace-board';
import { WorkspaceBoardSkeleton } from '~/modules/task/board/workspace-board-skeleton';
import { WorkspaceBoardTabs } from '~/modules/task/board/workspace-board-tabs';
import { TaskSearchScope } from '~/modules/task/task-search-scope';
import { flattenInfiniteData } from '~/query/basic/flatten';

export interface BoardProps {
  boardId: string;
  projects?: EnrichedProject[];
  workspace?: Workspace;
  publicView?: boolean;
}

export type ResolvedBoardProps = Omit<BoardProps, 'projects'> & { projects: EnrichedProject[] };

/**
 * Main task board component that conditionally renders desktop or mobile views based on screen size.
 */
export function Board({ boardId, projects: projectsProp, workspace, publicView }: BoardProps) {
  const isMobile = useBreakpointBelow('sm');
  const router = useRouter();
  const setActiveBoard = useBoardStore((state) => state.setActiveBoard);

  // Preload BlockNoteFullHtml so expanding a task card doesn't show a spinner
  usePreloadLazyComponents([BlockNoteFullHtml]);

  // Fetch projects for workspace boards; use provided projects for single-project boards
  const { data: fetchedData, isPending } = useInfiniteQuery({
    ...projectsListQueryOptions({ workspaceId: workspace?.id, include: 'counts', excludeArchived: 'true' }),
    enabled: !projectsProp,
  });
  const projects = projectsProp ?? flattenInfiniteData<Project>(fetchedData);
  const isLoadingProjects = !projectsProp && isPending;

  useEffect(() => {
    setActiveBoard(boardId, workspace ? 'workspace' : 'project');
  }, [boardId, workspace, setActiveBoard]);

  // One board-level subscription closes the mobile create-task dialog on navigation,
  // avoiding one subscription per panel action.
  useEffect(() => router.subscribe('onBeforeLoad', () => useDialoger.getState().remove('create-task')), [router]);

  const BoardView = (() => {
    if (isLoadingProjects && workspace) return <WorkspaceBoardSkeleton workspace={workspace} withHeader={false} />;
    if (isLoadingProjects) return <BoardSkeleton boardId={boardId} withHeader={false} publicView={publicView} />;
    if (!projects.length) return <BoardEmpty workspace={workspace} publicView={publicView} />;
    if (isMobile) return <WorkspaceBoardTabs projects={projects} workspace={workspace} publicView={publicView} />;
    if (!workspace) return <ProjectBoard boardId={boardId} projects={projects} publicView={publicView} />;
    return <WorkspaceBoard boardId={boardId} projects={projects} workspace={workspace} publicView={publicView} />;
  })();

  // Card layout and form animations follow the system's reduced motion setting.
  return (
    <MotionConfig reducedMotion="user">
      <TaskSearchScope>
        <BoardHeader boardId={boardId} projects={projects} workspace={workspace} publicView={publicView} />
        {BoardView}
      </TaskSearchScope>
    </MotionConfig>
  );
}
